import { RestUser } from '../schema-copy/api-schema';
import { LoginResult, RestMembership, AccountStatus, RestAccount, RestAccountUpdate, RestInvitationPreview } from '../schema-copy/auth-schema';
import { clearCache, FetchError, fetchWithTimeout, sanitizeEmail, sanitizeNoWhitespace, sanitizePhoneNumber, sanitizeSingleLine } from 't-lib-ui/connector';
import { logOutPreferences } from './preferences';
import { getJurisdiction } from './geo';

export const MAGIC_SECRET_KEY = 'magicAdminSecret';

export function getConfig(): {authHost: string, webHost: string, initApiHost: string, initApiHostEu: string, cdnUrl: string} {
    const configElement = document.getElementById('config')?.dataset;
    const initApiHost = configElement?.initApiHost || process.env.REACT_APP_INIT_API_HOST || 'http://localhost:3034';
    return {
        authHost: configElement?.authHost || process.env.REACT_APP_AUTH_HOST || 'http://localhost:3032',
        webHost: configElement?.webHost || process.env.REACT_APP_WEB_HOST || 'http://localhost:3000',
        initApiHost,
        // EU anonymous API instance. Anonymous EU visitors are routed here (data residency); everyone
        // else uses initApiHost. Locally there's a single API server, so this falls back to it.
        initApiHostEu: configElement?.initApiHostEu || process.env.REACT_APP_INIT_API_HOST_EU || initApiHost,
        // Global CDN base (shared images, discover JSON, feed content). Logged-in users get a
        // per-membership cdnUrl from auth; anonymous users fall back to this global value. The
        // deployed build injects the authoritative value via the #config element (set in infra);
        // this default is only for local dev without that injection.
        cdnUrl: configElement?.cdnUrl || process.env.REACT_APP_CDN_URL || 'https://data.downread.com/',
    };
}

// This is the Downread frontend, so memberships and appUrls are keyed by this app id.
const DOWNREAD_APP_ID = 'downread';

// A usable host is a non-empty string that isn't an unresolved <instance> template (that placeholder
// is only meaningful once a membership/instance exists, which it does on membership.urls but may not
// on the pre-membership appUrls fallback).
function usableHost(host?: string): string | undefined {
    return host && !host.includes('<instance>') ? host : undefined;
}

export function getApiHost(auth: AuthData): string {
    // Prefer the selected membership's apiHost (instance-resolved by the auth server). Before a
    // membership exists (e.g. the createUser call right after signup), fall back to the jurisdiction
    // app URLs the auth server returned, then to the configured default.
    const host = usableHost(auth.selectedMembership?.urls?.apiHost) ?? usableHost(auth.appUrls?.[DOWNREAD_APP_ID]?.apiHost);
    if (host) return host;
    console.warn('Auth service did not provide API URLs. Using initApiHost.');
    return getConfig().initApiHost;
}

// Logged-in users get their per-membership CDN url from auth; anonymous users (no auth) fall back
// to the global CDN base from config. The CDN host is the same for everyone, so the fallback is safe.
export function getCdnUrl(auth?: AuthData): string {
    return auth?.selectedMembership?.urls?.cdnUrl || auth?.appUrls?.[DOWNREAD_APP_ID]?.cdnUrl || getConfig().cdnUrl;
}

export interface AuthData {
    id: string;
    memberships: RestMembership[];
    selectedMembership?: RestMembership;
    status: AccountStatus;
    code?: string; // test use only
    tag?: string; // used to identify test data etc
    // Per-app URL map for the account's jurisdiction (from the auth server). Used to reach the right
    // API server before a membership exists (the first createUser after signup). See getApiHost.
    appUrls?: Record<string, Record<string, string>>;
};

// The JWT lives ONLY here — a module-scoped variable, never in localStorage or on `window`, so page
// scripts / XSS cannot read it. It is obtained from /auth/refresh (cookie-authenticated) on boot and
// re-obtained on expiry; the HttpOnly authToken cookie is the durable credential that survives reload.
let currentJwt: string | undefined;
// The token's `exp` claim (ms epoch), parsed once when the JWT is set, so we can refresh proactively
// instead of sending a known-expired token and eating a 401+retry. undefined if unparseable.
let currentJwtExpMs: number | undefined;
export function getJwt(): string | undefined { return currentJwt; }
export function setJwt(jwt: string | undefined): void { currentJwt = jwt; currentJwtExpMs = jwt ? parseJwtExpMs(jwt) : undefined; }

// Decode a JWT's `exp` (seconds) into ms epoch. Returns undefined for a malformed token, in which
// case callers fall back to sending it (the server's 401 refresh path still recovers).
function parseJwtExpMs(jwt: string): number | undefined {
    const payload = jwt.split('.')[1];
    if (!payload) return undefined;
    try {
        const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
        return typeof json.exp == 'number' ? json.exp * 1000 : undefined;
    } catch { return undefined; }
}

// Whether the in-memory JWT is expired (or within the safety margin of expiring). The margin covers
// the request's own flight time so the token doesn't expire server-side mid-request. A skewed client
// clock at worst triggers an extra refresh — never a loop (the refresh path passes isRetry).
const JWT_EXPIRY_MARGIN_MS = 10_000;
function isJwtExpired(): boolean {
    return currentJwtExpMs != null && Date.now() >= currentJwtExpMs - JWT_EXPIRY_MARGIN_MS;
}

// The email is kept in localStorage only as an opaque blob, encrypted with the account's symmetricKey
// (which stays server-side). To read it the client posts the blob to /auth/email and gets the
// plaintext back. The decrypted value is cached in memory only (never persisted) to avoid re-posting.
const EMAIL_ENC_KEY = 'email-enc';
let decryptedEmailCache: string | undefined;

function storeEncryptedEmail(encryptedEmail?: string): void {
    decryptedEmailCache = undefined; // invalidate; the blob (and thus plaintext) may have changed
    if (encryptedEmail) window.localStorage.setItem(EMAIL_ENC_KEY, encryptedEmail);
}

// Whether an encrypted email blob is stored locally (so callers can show a "decrypting…" state
// instead of "no email" while getEmail() resolves). Returns the already-decrypted value if cached.
export function hasEncryptedEmail(): boolean {
    return decryptedEmailCache != null || !!window.localStorage.getItem(EMAIL_ENC_KEY);
}

// The plaintext email we already hold in memory (decrypted earlier, or seeded from what the user
// just typed at login). Lets the Account page render the address instantly, without the
// cookie-authenticated /auth/email round-trip — which fails on iOS PWAs that drop the cross-site
// authToken cookie.
export function getCachedEmail(): string | undefined { return decryptedEmailCache; }

// Seed that cache from a known plaintext address (the one entered at login/signup). Must be called
// AFTER storeEncryptedEmail (which clears the cache), so the order in login()/signUp() matters.
export function cacheDecryptedEmail(email?: string): void {
    if (email) decryptedEmailCache = email.toLowerCase();
}

export async function getEmail(auth: AuthData): Promise<string | undefined> {
    if (decryptedEmailCache != null) return decryptedEmailCache;
    const blob = window.localStorage.getItem(EMAIL_ENC_KEY);
    if (!blob) return undefined;
    const r = await authenticatedApiPost<{email?: string}>(auth, 'auth', '/auth/email', { encryptedEmail: blob });
    decryptedEmailCache = r.email;
    return r.email;
}

export function persistAuth(auth: AuthData) {
    window.localStorage.setItem('auth', JSON.stringify(auth));
}
export function persistUser(user: RestUser) {
    window.localStorage.setItem('user', JSON.stringify(user));
}
export function unpersistUser() {
    window.localStorage.removeItem('user');
    window.localStorage.removeItem('team');
}

export function getAuthFromStorage(): AuthData | undefined {
    const auth = window.localStorage.getItem('auth');
    if (!auth)
        return undefined;
    const parsed: AuthData = JSON.parse(auth);
    if (parsed.selectedMembership && !parsed.selectedMembership.urls) {
        window.localStorage.removeItem('auth');
        unpersistUser();
        return undefined;
    }
    return parsed;
}
export function getUserFromStorage(): RestUser | undefined {
    const user = window.localStorage.getItem('user');
    if (!user)
        return undefined;
    return JSON.parse(user);
}

/**
 * The app runs in one of two modes. Logged-in = both the `auth` and `user` keys are present in
 * localStorage; otherwise the user is anonymous (the default for a first-time visitor — no extra
 * flag). This is the single source of truth for which feed engine to instantiate.
 */
export function getMode(): 'auth' | 'anon' {
    return getAuthFromStorage() && getUserFromStorage() ? 'auth' : 'anon';
}

// A durable "this browser holds a real account session" hint kept in localStorage. localStorage
// outlives the cross-site authToken cookie, which iOS/Safari readily evicts (especially for an
// installed PWA). On boot we use it to tell an evicted-cookie session ("should be logged in, but the
// cookie is gone") apart from a genuine anonymous visitor, so we can require re-login instead of
// silently dropping to anonymous mode. Cleared by logOut()'s localStorage.clear().
const LOGGED_IN_KEY = 'logged-in';
export function markLoggedIn(): void { window.localStorage.setItem(LOGGED_IN_KEY, '1'); }
export function wasLoggedIn(): boolean { return !!window.localStorage.getItem(LOGGED_IN_KEY); }

function getAuthHeaders(): Record<string, string> {
    return currentJwt ? { Authorization: `Bearer ${currentJwt}` } : {};
}

export function getAdminMagicSecret(): string | null {
    return window.localStorage.getItem(MAGIC_SECRET_KEY);
}
export function setAdminMagicSecret(secret: string): void {
    window.localStorage.setItem(MAGIC_SECRET_KEY, secret);
}
export function clearAdminMagicSecret(): void {
    window.localStorage.removeItem(MAGIC_SECRET_KEY);
}

// The authToken cookie now points at a different account than our stored JWT — typically
// because another tab logged in and replaced the HttpOnly cookie. Reload so the app re-reads
// its state for whichever account the cookie identifies. Returns true when it triggered.
function reloadOnCookieMismatch(json: any): boolean {
    if (json?.reload || json?.type == 'CookieMismatch') {
        window.location.reload();
        return true;
    }
    return false;
}

export async function refreshAuth(auth: AuthData): Promise<AuthData> {
    // The cookie authenticates; the body tells the server which membership to select (needed on a
    // cold start where no JWT is sent, so it can't read the selection from the token).
    const sel = auth.selectedMembership;
    const doRefresh = (body?: object) => fetch(`${getConfig().authHost}/auth/refresh`, {
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        method: 'POST',
        credentials: 'include', // send the HttpOnly authToken cookie (cross-origin to the auth host)
        ...(body ? { body: JSON.stringify(body) } : {}),
    });
    let r = await doRefresh(sel ? { memberId: sel.memberId, teamId: sel.teamId } : undefined);
    // The stored membership can belong to a DIFFERENT account than the cookie: another tab (or app
    // context sharing the cookie jar) logged in as someone else, and this tab reboots with the old
    // auth still in storage. The server then rejects the unknown memberId (NotFound) — retry
    // identity-only: the cookie resolves the account and its single membership auto-selects.
    // Not on 401: that's the cookie itself failing, which the mismatch/relogin handling below owns.
    if (!r.ok && sel && r.status != 401)
        r = await doRefresh();
    if (!r.ok) {
        if (r.status == 401 && r.headers.get('content-type')?.startsWith('application/json')
                && reloadOnCookieMismatch(await r.clone().json().catch(() => undefined)))
            throw new Error('Cookie mismatch, reloading');
        throw await FetchError.fromResponse(r);
    }
    const result = await r.json() as LoginResult;
    if (!result.authToken)
        throw new Error('Invalid response');
    setJwt(result.authToken); // JWT held in memory only, never persisted
    // Trust the cookie-authenticated refresh: it is always for the account the cookie identifies (a
    // different account is caught server-side as CookieMismatch -> reload). Do NOT log out here on a
    // membership check — that would clear the JWT while leaving the UI logged in, breaking every
    // subsequent request. Preserve the current selectedMembership if the refresh didn't resolve one
    // (e.g. a multi-membership account the server couldn't disambiguate) so getApiHost still works.
    const currentAuth = {
        id: result.id,
        memberships: result.memberships,
        selectedMembership: result.memberships.find(m => m.memberId === result.selectedMembershipId) ?? auth.selectedMembership,
        status: result.status,
        appUrls: result.appUrls ?? auth.appUrls,
    };
    persistAuth(currentAuth);
    markLoggedIn(); // cookie-authed refresh succeeded — (re)assert the durable logged-in hint
    return currentAuth;
}

// Coalesce concurrent refreshes into a single /auth/refresh round-trip. A burst of api requests can
// all discover the in-memory JWT is missing or expired at once (every admin query firing together, a
// page racing boot, or an HMR reload in dev resetting the module-scoped currentJwt). Without this they
// would each fire their own refresh; here they share the first one in flight.
let pendingRefresh: Promise<AuthData> | undefined;
function refreshAuthOnce(auth: AuthData): Promise<AuthData> {
    if (!pendingRefresh)
        pendingRefresh = refreshAuth(auth).finally(() => { pendingRefresh = undefined; });
    return pendingRefresh;
}

function removeNulls<T>(obj: Partial<T>): T {
    const result: any = {};
    for (const key in obj)
        if (obj[key] != null)
            result[key] = obj[key];
    return result;
}

// apiHostOverride: when host == 'api', send to this exact base URL (e.g. "https://api02.downread.com")
// instead of the auth-membership's apiHost. Used by admin tooling to target a specific API instance.
export async function authenticatedRequest<T>(auth: AuthData, method: string, host: 'auth'|'api', path: string, isRetry = false, data?: any, timeout?: number, apiHostOverride?: string): Promise<T> {
    // The api host accepts only the in-memory JWT (it never gets the cookie). Recover/renew it from the
    // cookie BEFORE sending when it's either missing — a request racing boot, a second tab, or an HMR
    // reload clearing the module state (else the server hard-fails with "Missing Authorization header",
    // since it only returns the retriable refresh:newToken for a present-but-expired token) — or already
    // expired, e.g. a PWA/tab resuming after the 1h token lifetime, where we'd otherwise send a stale
    // token and pay a wasted 401+retry round-trip. Skip on a retry to avoid looping.
    if ((!getJwt() || isJwtExpired()) && !isRetry) {
        const newAuth = await refreshAuthOnce(auth);
        return authenticatedRequest(newAuth, method, host, path, true, data, timeout, apiHostOverride);
    }
    const usesUrlParams = /^(GET|DELETE)$/i.test(method);
    const baseUrl = `${host == 'api' ? (apiHostOverride ?? getApiHost(auth)) : getConfig().authHost}${path}`;
    const url = `${baseUrl}${usesUrlParams && data ? '?'+new URLSearchParams(removeNulls(data)) : ''}`;
    console.debug(method, url, `(authenticated request, ${new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })})`);
    const r = await fetchWithTimeout(url, {
        method,
        headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
        },
        // Only auth-host calls need the cookie; the api host's CORS doesn't allow credentials.
        ...(host == 'auth' ? {credentials: 'include' as const} : {}),
        ...((data && !usesUrlParams) ? {body: JSON.stringify(removeNulls(data))} : {}),
        ...(timeout ? {timeout} : {}),
    });
    if (!r.ok) {
        if (r.status === 401 && r.headers.get('content-type')?.startsWith('application/json') && !isRetry) {
            const json = await r.json();
            if (reloadOnCookieMismatch(json))
                throw new Error('Cookie mismatch, reloading');
            if (json.refresh === 'newToken') {
                const newAuth = await refreshAuthOnce(auth);
                return authenticatedRequest(newAuth, method, host, path, true, data, timeout, apiHostOverride);
            }
            throw FetchError.fromJson(r.status, json);
        }

        throw await FetchError.fromResponse(r);
    }
    return await r.json() as T;
}

export async function unauthenticatedRequest<T>(method: string, host: 'auth'|'api', path: string, data?: any, timeout?: number): Promise<T> {
    const usesUrlParams = /^(GET|DELETE)$/i.test(method);
    // Anonymous api requests route to the EU instance for EU visitors (data residency); the auth host
    // is single-region. Authenticated requests pick their host from the account (authenticatedRequest).
    const apiHost = host == 'api'
        ? (getJurisdiction() == 'EU' ? getConfig().initApiHostEu : getConfig().initApiHost)
        : getConfig().authHost;
    const baseUrl = `${apiHost}${path}`;
    const url = `${baseUrl}${usesUrlParams && data ? '?'+new URLSearchParams(removeNulls(data)) : ''}`;
    console.debug(method, url, `(unauthenticated request, ${new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })})`);
    const r = await fetchWithTimeout(url, {
        method,
        headers: {
            'Content-Type': 'application/json',
        },
        ...(host == 'auth' ? {credentials: 'include' as const} : {}),
        ...((data && !usesUrlParams) ? {body: JSON.stringify(removeNulls(data))} : {}),
        ...(timeout ? {timeout} : {}),
    });
    if (!r.ok) {
        throw await FetchError.fromResponse(r);
    }
    return await r.json() as T;
}

export async function authenticatedApiGet<T>(auth: AuthData, host: 'auth'|'api', path: string, parameters?: any): Promise<T> {
    return await authenticatedRequest<T>(auth, 'GET', host, path, false, parameters);
}

export async function authenticatedApiDelete<T>(auth: AuthData, host: 'auth'|'api', path: string, parameters?: any): Promise<T> {
    return await authenticatedRequest<T>(auth, 'DELETE', host, path, false, parameters);
}

export async function authenticatedApiPost<T>(auth: AuthData, host: 'auth'|'api', path: string, body: any): Promise<T> {
    return await authenticatedRequest<T>(auth, 'POST', host, path, false, body);
}

export async function authenticatedApiPut<T>(auth: AuthData, host: 'auth'|'api', path: string, body: any): Promise<T> {
    return await authenticatedRequest<T>(auth, 'PUT', host, path, false, body);
}

async function postRequest<T>(path: string, body: any, host: 'auth'|'api') {
    const url = `${getConfig().authHost}${path}`;
    const r = await fetchWithTimeout(url, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: {
            'Content-Type': 'application/json'
        },
        credentials: 'include', // login/signup/etc. set the HttpOnly authToken cookie on the response
    });
    if (!r.ok)
        throw await FetchError.fromResponse(r);
    return await r.json() as T;
}
async function executeLogin(auth: AuthData|undefined, path: string, body: any): Promise<{authData: AuthData, user?: RestUser}> {
    const result = auth ? await authenticatedApiPost<LoginResult>(auth, 'auth', path, body) : await postRequest<LoginResult>(path, body, 'auth');
    setJwt(result.authToken); // JWT held in memory only; the cookie (set on this response) is durable
    storeEncryptedEmail(result.encryptedEmail); // encrypted email blob for the Account page (no plaintext)
    const currentAuth = {
        id: result.id,
        memberships: result.memberships,
        selectedMembership: result.memberships.find(m => m.memberId === result.selectedMembershipId),
        status: result.status,
        code: result.code,
        tag: result.tag,
        appUrls: result.appUrls,
    };
    persistAuth(currentAuth);
    if (currentAuth.selectedMembership) {
        const user = await authenticatedApiGet<RestUser>(currentAuth, 'api', '/api/user');
        persistUser(user);
        return {authData: currentAuth, user};
    }
    else {
        return {authData: currentAuth};
    }
}

export async function login(emailOrPhone: string, password: string): Promise<{authData: AuthData, user?: RestUser}> {
    logOut();
    const email = sanitizeEmail(emailOrPhone);
    const phone = sanitizePhoneNumber(emailOrPhone);
    if (!email && !phone)
        throw new Error('Invalid email or phone number');

    const body = { ...(email ? {email} : {}), ...(phone ? {phone} : {}), password: sanitizeSingleLine(password), appId: 'downread' };
    const r = await executeLogin(undefined, `/auth/login`, body);
    cacheDecryptedEmail(email); // show the address on the Account page without a cookie round-trip
    return r;
}

// Local-only cleanup. Also runs as a pre-login/pre-signup clear, so it must NOT call the server
// (rotating the cookie there would race the immediately-following login). Use serverLogout() for an
// explicit, user-initiated logout that should also invalidate the server session.
export function logOut(): void {
    // Flush any pending (not-yet-synced) preference edits to the server FIRST, while the in-memory
    // JWT is still set. The flush is an authenticated PUT whose Authorization header is built
    // synchronously inside this call (useMutation -> authenticatedRequest -> getJwt run sync up to
    // the fetch), so clearing the JWT before it would 401 and silently lose the user's changes.
    logOutPreferences();
    setJwt(undefined); // drop the in-memory JWT (the flush above already captured it)
    decryptedEmailCache = undefined; // the email-enc key is wiped by localStorage.clear() below
    clearCache();
    // Wipe every browser-stored value EXCEPT the anonymous session (`anon-*` keys), on purpose.
    // This runs from manual logout, login/signup, and post-account-deletion. We don't want any
    // auth leftovers — auth, user, team, magic admin secret, last-used email, per-user
    // preferences, OPML pending imports, UI-only state (sidebar/column widths), etc.
    // But the anon library must survive: logging in keeps it dormant so logging out restores
    // the anon session, and signup reads it to migrate before clearing it explicitly
    // (see anon-migration.ts). The explicit anon "delete my data" path calls clearAnonData()
    // alongside this for a full wipe.
    const anonData: Record<string, string> = {};
    for (let i = 0; i < window.localStorage.length; i++) {
        const k = window.localStorage.key(i);
        if (k?.startsWith('anon-')) anonData[k] = window.localStorage.getItem(k)!;
    }
    // The resolved geo is kept in sessionStorage across logout on purpose, so the region decision
    // survives a logout while the tab stays open (the localStorage copy is intentionally cleared, and
    // gets re-mirrored from sessionStorage on the next read — see connector/geo.ts).
    const sessionGeo = window.sessionStorage.getItem('geo');
    window.localStorage.clear();
    window.sessionStorage.clear();
    for (const k in anonData) window.localStorage.setItem(k, anonData[k]);
    if (sessionGeo) window.sessionStorage.setItem('geo', sessionGeo);
}

// Explicit user logout: invalidate the server session too (rotate authToken + clear the cookie). The
// cookie is sent by the browser regardless of local state, so we clear locally immediately and fire
// the server call best-effort. Idempotent server-side; safe even if the cookie is already gone.
export function serverLogout(): void {
    try { fetch(`${getConfig().authHost}/auth/logout`, { method: 'POST', credentials: 'include' }).catch(() => {}); }
    catch { /* ignore */ }
    logOut();
}

export async function enableUser(auth: AuthData, membership: { memberId: string, teamId: string }): Promise<{authData: AuthData, user?: RestUser}> {
    // Preserve the encrypted-email blob across the logOut below. /auth/switch is a same-account
    // membership switch and can't re-issue the blob (the server holds only the email hash), so without
    // this a signup→confirm (which runs enableUser right after confirming) would drop the blob and the
    // Account page would show "(No email set)" until the next real login. Same pattern as confirmEmail().
    const savedEmailBlob = window.localStorage.getItem(EMAIL_ENC_KEY) || undefined;
    logOut();
    const body = { memberId: membership.memberId, teamId: membership.teamId, appId: 'downread' };
    const r = await executeLogin(auth, `/auth/switch`, body);
    if (savedEmailBlob && !window.localStorage.getItem(EMAIL_ENC_KEY)) storeEncryptedEmail(savedEmailBlob);
    return r;
}

/**
 * Polls until the membership is enabled, with retries. This is required because after creating a new user in the API, using the existing auth token,
 * it takes a while until the API server registered the membership with the auth server.
 * @param auth the existing auth
 * @param membership the user data to enable
 * @param retriesLeft for recursion
 * @param waitMore to have a bit shorter initial wait
 * @returns the new auth and user
 */
export async function tryEnableMembership(auth: AuthData, membership: { memberId: string, teamId: string }, retriesLeft = 4, waitMore = false): Promise<{authData: AuthData, user: RestUser}> {
    await new Promise(resolve => setTimeout(resolve, waitMore ? 1500 : 500));

    try {
      const r = await enableUser(auth, membership);
      if (!r.user)
        throw new Error('No user');
      return {authData: r.authData, user: r.user};
    } catch (e) {
      if (e instanceof FetchError && e.type != 'Connection' && retriesLeft) {
        return await tryEnableMembership(auth, membership, retriesLeft - 1, true);
      }
      throw e;
    }
  };

export async function signUp(email: string, password: string | undefined, nickName?: string,
        firstName?: string, lastName?: string, tag?: string, invitationCode?: string, secret?: string,
        jurisdiction?: string, requireEmailConfirmation?: boolean, turnstileToken?: string): Promise<AuthData> {
    logOut();

    const body = {
        email: sanitizeEmail(email),
        ...(password ? { password: sanitizeSingleLine(password) } : {}),
        nickName: sanitizeSingleLine(nickName),
        firstName: sanitizeSingleLine(firstName),
        lastName: sanitizeSingleLine(lastName),
        tag: sanitizeSingleLine(tag),
        ...(secret ? { secret: sanitizeSingleLine(secret) } : {}),
        // Required for real signups (sent from the accept-terms page); test signups bypass it.
        ...(turnstileToken ? { turnstileToken: sanitizeSingleLine(turnstileToken) } : {}),
        appId: 'downread',
        ...(invitationCode ? { invitationCode: sanitizeSingleLine(invitationCode) } : {}),
        // Test-signup knobs (only honored server-side with a valid tag+secret): force data residency
        // and require real email confirmation so the mailbox-based e2e flows can be exercised.
        ...(jurisdiction ? { jurisdiction: sanitizeSingleLine(jurisdiction) } : {}),
        ...(requireEmailConfirmation ? { requireEmailConfirmation: true } : {}),
    };
    const {authData} = await executeLogin(undefined, `/auth/signup`, body);
    cacheDecryptedEmail(sanitizeEmail(email));
    if (authData.code) {
        console.log('Sign up email confirmation code: ', authData.code);
        console.log(`Confirmation URL: ${getConfig().webHost}/confirm-email?userId=${encodeURIComponent(authData.id)}&confirmationCode=${encodeURIComponent(authData.code)}`);
    }
    return authData;
}

/**
 * Looks up a public preview (name + plan) for an invitation code, for the banner
 * on the sign-up page. Returns undefined when the code is unknown/expired/used
 * (the server answers 404 for all of those, so we simply show no banner).
 */
export async function getInvitationPreview(code: string): Promise<RestInvitationPreview | undefined> {
    try {
        return await unauthenticatedRequest<RestInvitationPreview>('GET', 'auth', '/auth/invitation', { code: sanitizeSingleLine(code), appId: 'downread' });
    } catch (e) {
        if (e instanceof FetchError && e.status == 404)
            return undefined;
        throw e;
    }
}

export async function confirmEmail(auth: AuthData, code: string, wasntMe = false, urlEmailBlob?: string): Promise<LoginResult> {
    // Confirm can't re-issue the encrypted-email blob (the server only has the email hash), so recover
    // it from, in order: the confirmation LINK's fragment (urlEmailBlob, which belongs to the account
    // being confirmed — see sendWelcomeEmail), else the signup's locally-stored blob (same-browser code
    // entry, where there's no link). The link's blob MUST win over the stored one: when a logged-in
    // user clicks a link for a DIFFERENT account, the stored blob is the old account's and would show
    // the wrong email. Captured before logOut wipes localStorage.
    const savedEmailBlob = window.localStorage.getItem(EMAIL_ENC_KEY) || undefined;
    logOut();
    const body = { id: auth.id, confirmationToken: sanitizeNoWhitespace(code, 16), wasntMe };
    // Confirm is self-authenticating: the server identifies the account from body.id and treats the
    // confirmationToken itself as the credential — no JWT needed. Must be an UNauthenticated request:
    // logOut() above just cleared the in-memory JWT, so authenticatedApiPost would fire a pointless
    // /auth/refresh first, which fails in a fresh browser opening the welcome link (no session cookie)
    // and the confirm would never run.
    const result = await unauthenticatedRequest<LoginResult>('POST', 'auth', `/auth/confirm`, body);
    if (result.authToken) setJwt(result.authToken); // confirm logs the user in; hold the new JWT in memory
    if (!wasntMe) storeEncryptedEmail(result.encryptedEmail ?? urlEmailBlob ?? savedEmailBlob);
    return result;
}

export async function requestOneTimePassword(email: string): Promise<{password?: string}> {
    const body = { email: sanitizeEmail(email) };
    const response = await postRequest<{password?: string}>(`/auth/password1`, body, 'auth');
    if (response.password)
        console.log(`One-time password: ${response.password}`);
    return response;
}

export async function loginWithOneTimePassword(emailOrPhone: string, password: string): Promise<{authData: AuthData, user?: RestUser}> {
    logOut();
    const email = sanitizeEmail(emailOrPhone);
    const phone = sanitizePhoneNumber(emailOrPhone);
    if (!email && !phone)
        throw new Error('Invalid email or phone number');

    const body = { ...(email ? {email} : {}), ...(phone ? {phone} : {}), password: sanitizeNoWhitespace(password, 6), appId: 'downread' };
    const r = await executeLogin(undefined, `/auth/login1`, body);
    cacheDecryptedEmail(email);
    return r;
}

// tosAccepted: set after the user accepted the ToS on the accept-terms page — required by the
// server to CREATE a new Google account (returning users log in without it, see TermsNotAccepted).
export async function googleLogin(idToken: string, invitationCode?: string, tosAccepted?: boolean): Promise<{authData: AuthData, user?: RestUser}> {
    logOut();
    const body = { idToken, appId: 'downread', ...(invitationCode ? { invitationCode: sanitizeSingleLine(invitationCode) } : {}), ...(tosAccepted ? { tosAccepted: true } : {}) };
    const r = await executeLogin(undefined, `/auth/google`, body);
    // executeLogin already stored the encrypted email blob from the google LoginResult, so the
    // Account page resolves the right address via getEmail() — no plaintext copy needed here.
    return r;
}

export async function requestPasswordReset(emailOrPhone: string): Promise<{}> {
    logOut();
    const body = emailOrPhone.includes('@') ? { email: sanitizeEmail(emailOrPhone) } : { phone: sanitizePhoneNumber(emailOrPhone) };
    const response = await postRequest<{resetToken?: string, id?: string}>(`/auth/requestPasswordReset`, body, 'auth');
    if (response.resetToken)
        console.log(`Reset token: ${response.resetToken}\nid: ${response.id}
        URL: http://localhost:3000/reset-password?userId=${encodeURIComponent(response.id||'')}&code=${encodeURIComponent(response.resetToken||'')}`);
    return response;
}
export async function resetPassword(identity: {id?: string; email?: string}, resetToken: string, password: string): Promise<{}> {
    logOut();
    const body: {id?: string; email?: string; resetToken: string; password: string} = {
        resetToken: sanitizeNoWhitespace(resetToken, 32),
        password: sanitizeSingleLine(password, 128),
    };
    if (identity.id) body.id = sanitizeNoWhitespace(identity.id, 64);
    else if (identity.email) body.email = sanitizeEmail(identity.email);
    return await postRequest<{}>(`/auth/resetPassword`, body, 'auth');
}

export async function getCurrentAccount(auth: AuthData): Promise<RestAccount> {
    return await authenticatedApiGet<RestAccount>(auth, 'auth', '/auth/account', {});
}

export async function getCurrentUser(auth: AuthData): Promise<RestUser> {
  return await authenticatedApiGet<RestUser>(auth, 'api', '/api/user', {});
}

export async function requestEmailChange(auth: AuthData, oldEmail: string, newEmail: string, password: string): Promise<'LoginTimeout'|'LoginFailed'|'Conflict'|undefined> {
    const body = {
        oldEmail: sanitizeEmail(oldEmail),
        newEmail: sanitizeEmail(newEmail),
        password: sanitizeSingleLine(password),
    };
    try {
        const r = await authenticatedApiPost<{codeA?: string, codeB?: string}>(auth, 'auth', '/auth/requestEmailChange', body);
        if (r.codeA && r.codeB)
            console.log(`Email change codes: ${r.codeA} / ${r.codeB}`);
    }
    catch (e) {
        if (e instanceof FetchError && e.type == 'LoginTimeout') return 'LoginTimeout';
        if (e instanceof FetchError && e.type == 'LoginFailed') return 'LoginFailed';
        if (e instanceof FetchError && e.type == 'InvalidRequest') return 'Conflict';
        throw e;
    }
}

export async function changeEmail(auth: AuthData, newEmail: string, codeA: string, codeB: string): Promise<'LoginTimeout'|'LoginFailed'|'TokenInvalid'|undefined> {
    const body = {
        newEmail: sanitizeEmail(newEmail),
        codeA: sanitizeNoWhitespace(codeA, 16),
        codeB: sanitizeNoWhitespace(codeB, 16),
    };
    try {
        // changeEmail rotates the cookie (server set the new one on this response) and re-issues the
        // JWT; adopt it so we keep a valid in-memory token for subsequent api calls.
        const r = await authenticatedApiPost<{authToken: string, encryptedEmail?: string}>(auth, 'auth', '/auth/changeEmail', body);
        if (r.authToken) setJwt(r.authToken);
        storeEncryptedEmail(r.encryptedEmail); // refresh the stored blob to the new address
    }
    catch (e) {
        if (e instanceof FetchError && e.type == 'LoginTimeout') return 'LoginTimeout';
        if (e instanceof FetchError && e.type == 'LoginFailed') return 'LoginFailed';
        if (e instanceof FetchError && e.type == 'TokenInvalid') return 'TokenInvalid';
        throw e;
    }
}

export async function verifyPassword(auth: AuthData, password: string): Promise<'LoginTimeout'|'LoginFailed'|undefined> {
    try {
        await authenticatedApiPost<{ok: true}>(auth, 'auth', '/auth/verifyPassword', { password: sanitizeSingleLine(password) });
    }
    catch (e) {
        if (e instanceof FetchError && e.type == 'LoginTimeout') return 'LoginTimeout';
        if (e instanceof FetchError && e.type == 'LoginFailed') return 'LoginFailed';
        throw e;
    }
}

export async function deleteAccount(auth: AuthData): Promise<{success: boolean}> {
    return await authenticatedApiDelete<{success: boolean}>(auth, 'api', '/api/user/self');
}

export async function updateAccount(auth: AuthData, account: RestAccountUpdate): Promise<'LoginTimeout'|'LoginFailed'|undefined> {
    const body = {
        ...account,
        ...(account.nickName ? {nickName: sanitizeSingleLine(account.nickName)} : {}),
        ...(account.firstName ? {firstName: sanitizeSingleLine(account.firstName)} : {}),
        ...(account.lastName ? {lastName: sanitizeSingleLine(account.lastName)} : {}),
        ...(account.password ? {password: sanitizeSingleLine(account.password)} : {}),
        ...(account.oldPassword ? {oldPassword: sanitizeSingleLine(account.oldPassword)} : {}),
    };
    try {
        await authenticatedApiPut<RestAccount>(auth, 'auth', '/auth/account', body);
    }
    catch (e) {
        if (e instanceof FetchError && e.type == 'LoginTimeout')
            return 'LoginTimeout';
        if (e instanceof FetchError && e.type == 'LoginFailed')
            return 'LoginFailed';
        throw e;
    }
}

